import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import {Link} from 'gatsby';
import {motion} from 'framer-motion';
import {b1} from 'constants/fonts';
import {useScrollBlock} from 'hooks';
import CursorProvider, {CursorContext} from 'contexts/CursorProvider';
import Flex from 'layout/Flex';

export type Category = {
  anchor:
    | 'video'
    | 'photography'
    | 'textile'
    | 'written-word'
    | 'sound'
    | 'sculpture'
    | 'mixed-media'
    | 'drawing'
    | 'painting';
  category:
    | 'Video'
    | 'Photography'
    | 'Textile'
    | 'WrittenWord'
    | 'Sound'
    | 'Sculpture'
    | 'Mixed Media'
    | 'Drawing'
    | 'Painting';
  count: number;
  imageSrc: string;
};
type Props = {categories: Category[]};

let interval: NodeJS.Timeout | undefined;

function WorkCategoriesComponent({categories}: Props) {
  const [blockScroll, allowScroll] = useScrollBlock();
  const [activeCategory, setActiveCategory] = useState(0);
  const {cursor} = useContext(CursorContext);

  useEffect(() => {
    blockScroll();
    return () => {
      allowScroll();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (interval) clearInterval(interval);
      interval = undefined;
    };
  }, []);

  useEffect(() => {
    if (categories.length > 0 && !interval) {
      createInterval();
    }
  }, [categories]);

  function createInterval() {
    interval = setInterval(() => {
      setActiveCategory(prevState => (prevState + 1 < categories.length ? prevState + 1 : 0));
    }, 3000);
  }

  function removeInterval() {
    if (interval) clearInterval(interval);
    interval = undefined;
  }

  function handleMouseOverLink(index: number) {
    setActiveCategory(index);
  }

  return (
    <Flex center css={{minHeight: 'calc(100vh - 132px)'}}>
      <Flex center css={{position: 'relative', height: '100%', width: '100%'}}>
        {categories.map(({anchor, category, imageSrc}, index) => {
          return (
            <motion.div
              animate={{
                opacity: activeCategory === index ? 1 : 0,
                scale: activeCategory === index ? 1 : 0.95,
                x: -cursor.cursorCenterDeltaX * 100,
                y: -cursor.cursorCenterDeltaY * 100,
              }}
              css={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                opacity: activeCategory === index ? 1 : 0,
                pointerEvents: activeCategory === index ? 'auto' : 'none',
                position: 'absolute',
                width: '100%',
              }}
              key={category + imageSrc}
              transition={{
                opacity: {duration: 0.2},
                scale: {duration: 0.2},
                x: {type: 'spring'},
                y: {type: 'spring'},
              }}
            >
              <Link css={{position: 'relative'}} to={`/works#${anchor}`}>
                <img
                  css={{
                    objectFit: 'contain',
                    objectPosition: 'center',
                    height: '100%',
                    width: '100%',
                    maxHeight: '70vh',
                    transform: 'translateY(-66px)', // half header
                    position: 'relative',
                  }}
                  key={category}
                  src={imageSrc}
                />
              </Link>
            </motion.div>
          );
        })}
      </Flex>
      <Flex
        direction="column"
        css={{
          justifyContent: 'center',
          position: 'relative',
          top: -66, // half height of header
          right: -16,
          width: 385,
        }}
      >
        {categories.map(({anchor, category}, index) => (
          <Link
            css={{
              paddingBottom: 24,
              '&:last-child': {
                paddingBottom: 0,
              },
            }}
            key={`${category}-link`}
            onMouseEnter={removeInterval}
            onMouseLeave={createInterval}
            onMouseOver={() => handleMouseOverLink(index)}
            to={`/works#${anchor}`}
          >
            <h3
              css={({color}) => [
                b1,
                {
                  color: activeCategory === index ? color.red : color.lightRed,
                  fontSize: 24,
                  textAlign: 'right',
                  textDecoration: 'none',
                  textTransform: 'uppercase',
                  transitionDuration: '500ms',
                  transitionProperty: 'all',
                  '&:hover': {
                    color: color.red,
                  },
                },
              ]}
            >
              {category}
            </h3>
          </Link>
        ))}
      </Flex>
    </Flex>
  );
}

export default function WorkCategories(props: Props) {
  return (
    <CursorProvider>
      <WorkCategoriesComponent {...props} />
    </CursorProvider>
  );
}
